// Marketing / partner-facing translations only.
// The rest of the app (guest, host, venue owner, admin) stays in English.
export const marketingTranslations = {
  en: {
    // Header nav
    'nav.home': 'Home',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.support': 'Support',
    'nav.login': 'Log in',
    'nav.get_started': 'Get started',

    // Footer
    'footer.column.allure': 'Allure',
    'footer.column.help': 'Help',
    'footer.link.home': 'Home',
    'footer.link.about': 'About us',
    'footer.link.contact': 'Contact',
    'footer.link.support': 'Support',
    'footer.link.privacy': 'Privacy policy',
    'footer.link.terms': 'Terms of use',
    'footer.link.imprint': 'Legal / Imprint',
    'footer.getInTouch': 'Get in touch',
    'footer.getTheApp': 'Get the app',
    'footer.replyPromise': "We typically reply within one business day.",
    'footer.brandTagline': 'The private guest app for travellers staying in Airbnb homes — invited by your host, discounted at every venue.',
    'footer.rights': 'All rights reserved.',
    'footer.crafted': 'Crafted for guests who travel well.',

    // Home
    'home.eyebrow': 'Allure',
    'home.title.line1': 'The missing piece',
    'home.title.line2': 'between staying somewhere',
    'home.title.line3': 'and truly experiencing it.',
    'home.subtitle': 'Allure transforms Airbnb stays into curated local experiences. Guests enter through a trusted network of hosts, venues, and experiences designed for travellers who want to live the area — not just visit it.',
    'home.cta.createAccount': 'Create your account',
    'home.cta.haveAccount': 'I already have an account',
    'home.cta.download': 'Download the app',
    'home.cta.orDownload': 'Or download the app',
    'home.cards.guests.eyebrow': 'For Guests',
    'home.cards.guests.title': "One pass. The area's best venues.",
    'home.cards.guests.b1': 'QR pass unlocking exclusive discounts at handpicked local venues',
    'home.cards.guests.b2': 'Unlock higher discounts the more you explore',
    'home.cards.guests.b3': 'Curated by locals — not tourist algorithms',
    'home.cards.guests.b4': 'Discover hidden gems, ready for you on the map',
    'home.cards.guests.b5': 'Tip your host directly inside the app',
    'home.cards.guests.b6': 'Reviews only from verified Allure guests',
    'home.cards.hosts.eyebrow': 'For Hosts',
    'home.cards.hosts.title': 'Help your guests explore. Earn while you sleep.',
    'home.cards.hosts.b1': 'Earn passive commission when guests use Allure',
    'home.cards.hosts.b2': 'Auto-sync bookings through your Airbnb calendar',
    'home.cards.hosts.b3': 'Deliver a premium experience with no extra work',
    'home.cards.hosts.b4': 'Boost your reviews and traveller satisfaction',
    'home.cards.hosts.b5': 'Let guests thank you with an in-app tip',
    'home.cards.venues.eyebrow': 'For Venues',
    'home.cards.venues.title': 'Reach verified travellers. Convert tourism into revenue.',
    'home.cards.venues.b1': 'Become one of the few places guests are consistently guided toward',
    'home.cards.venues.b2': 'Reach verified travellers already staying nearby',
    'home.cards.venues.b3': "Be recognised as one of the area's standout venues",
    'home.cards.venues.b4': 'Convert local tourism into direct in-venue revenue',
    'home.privacy.noAdTracking': 'No ad tracking',
    'home.privacy.noSellData': 'No selling user data',
    'home.privacy.gdpr': 'GDPR + CCPA compliant',
    'home.privacy.deletion': 'Full account deletion in-app',
    'home.vision.eyebrow': 'The Vision',
    'home.vision.title': 'Guests no longer arrive feeling lost. Hosts no longer rely only on bookings. Hidden local gems no longer stay hidden.',
    'home.vision.body': 'By connecting travellers, hosts, and carefully selected venues, Allure creates a hospitality ecosystem where everyone benefits through trust, discovery, and memorable experiences.',
    'home.vision.footer1': 'Allure is currently rolling out across Europe through an invite-only model.',
    'home.vision.footer2': 'Travel like a local. Belong wherever you stay.',
    'home.finalCta.eyebrow': 'Designed To Experience',
    'home.finalCta.title': 'Already got your invite? Open the app and step in.',

    // About
    'about.eyebrow': 'About us',
    'about.title': 'Allure exists for the traveller who refuses to settle for the obvious.',
    'about.p1': 'We started Allure because the gap between staying somewhere and truly experiencing it had become impossibly wide. Algorithms got louder. Reviews got noisier. Real recommendations got buried.',
    'about.p2': "So we built a private guest platform. Your host invites you to Allure once your stay is confirmed. Inside, you discover the venues a local would actually send you to — and scan your in-app QR at checkout to unlock discounts only Allure guests can access. No noise. No bait. Just hospitality, the way it was meant to be.",
    'about.values.title': 'What we believe.',
    'about.values.curated.title': 'Curated, not crowded',
    'about.values.curated.body': 'Every venue on Allure is hand-picked by locals who actually live, eat and drink in the area. No paid-for rankings. No tourist traps.',
    'about.values.trust.title': 'Trust by design',
    'about.values.trust.body': 'Reviews only come from verified Allure guests. Discounts are honoured at the door because every venue partners with us directly — scan your QR at the venue, save instantly.',
    'about.values.hospitality.title': 'Made with hospitality',
    'about.values.hospitality.body': 'Built alongside hosts, restaurants and travellers — the people Allure exists to serve. Their feedback shapes every release.',

    // Contact
    'contact.eyebrow': 'Contact',
    'contact.title': 'Say hello.',
    'contact.subtitle': 'Hosts, venues and curious travellers — we read every message. Reach out below or call us directly.',
    'contact.form.name': 'Your name',
    'contact.form.email': 'Email',
    'contact.form.message': 'Message',
    'contact.form.namePlaceholder': 'Alex Doe',
    'contact.form.emailPlaceholder': 'you@example.com',
    'contact.form.messagePlaceholder': "Tell us a little about why you're reaching out.",
    'contact.form.submit': 'Send message',
    'contact.form.submitting': 'Sending…',
    'contact.form.completeAll': 'Please complete every field',
    'contact.form.success': "Thanks — we'll be in touch soon.",
    'contact.form.error': 'Could not send your message right now',
    'contact.aside.eyebrow': 'Direct lines',
    'contact.aside.title': 'Talk to a human, not a chatbot.',
    'contact.aside.reply': 'We typically reply within one business day.',

    // Support (marketing)
    'support.eyebrow': 'Support',
    'support.title': "We're here when you need us.",
    'support.subtitle': "Most answers live below. If something's still unclear, message us directly and we'll reply within one business day.",
    'support.card1.title': 'Send us a message',
    'support.card1.body': "Drop into the contact form and we'll route it to the right team.",
    'support.card1.cta': 'Open contact form',
    'support.card2.title': 'Email support',
    'support.card2.body': 'Prefer email? Reach us anytime at contact@allurexp.com',
    'support.card2.cta': 'contact@allurexp.com',
    'support.card3.title': 'Read the FAQ',
    'support.card3.body': 'Quick answers to the most common Allure questions.',
    'support.card3.cta': 'Jump to FAQ',
    'support.faq.title': 'Frequently asked.',
    'support.faq1.q': 'How do I get into Allure?',
    'support.faq1.a': "When you book an Airbnb whose host is on Allure, your host sends you an invite to join. Accept it, create your account, and you're in for the length of your stay.",
    'support.faq2.q': 'How do I get the discount at a venue?',
    'support.faq2.a': 'Open the Allure app, pick the venue from the map, and pay your bill at the venue as normal. At checkout, show the staff your in-app QR code — they scan it and the Allure discount is applied to your bill instantly.',
    'support.faq3.q': 'Do I have to download the app from Google Play?',
    'support.faq3.a': 'You can use Allure on the web at allurexp.com or download the official Android app from Google Play. Both share the same account.',
    'support.faq4.q': 'Are the discounts really exclusive?',
    'support.faq4.a': 'Yes — venues honour the Allure discount because they partner with us directly. You will not find these rates publicly online.',
    'support.faq5.q': "I'm a host — how do I join?",
    'support.faq5.a': 'Tap "Get started" and select Host on the role screen. You\'ll need an Airbnb listing URL and iCal link so Allure can detect your reservations and invite your guests automatically.',
    'support.faq6.q': 'I run a venue. How do I become an Allure partner?',
    'support.faq6.a': 'Tap "Get started", choose Partner, then Venue, and our team reviews each application personally.',

    // Language switcher
    'lang.label': 'Language',
    'lang.english': 'English',
    'lang.greek': 'Ελληνικά',
  },
  el: {
    // Header nav
    'nav.home': 'Αρχική',
    'nav.about': 'Σχετικά',
    'nav.contact': 'Επικοινωνία',
    'nav.support': 'Υποστήριξη',
    'nav.login': 'Σύνδεση',
    'nav.get_started': 'Ξεκίνα',

    // Footer
    'footer.column.allure': 'Allure',
    'footer.column.help': 'Βοήθεια',
    'footer.link.home': 'Αρχική',
    'footer.link.about': 'Σχετικά με εμάς',
    'footer.link.contact': 'Επικοινωνία',
    'footer.link.support': 'Υποστήριξη',
    'footer.link.privacy': 'Πολιτική Απορρήτου',
    'footer.link.terms': 'Όροι Χρήσης',
    'footer.link.imprint': 'Νομικά',
    'footer.getInTouch': 'Επικοινώνησε μαζί μας',
    'footer.getTheApp': 'Κατέβασε την εφαρμογή',
    'footer.replyPromise': 'Συνήθως απαντάμε εντός μίας εργάσιμης ημέρας.',
    'footer.brandTagline': 'Η ιδιωτική guest εφαρμογή για ταξιδιώτες που διαμένουν σε Airbnb — προσκεκλημένοι από τον host τους, με έκπτωση σε κάθε συνεργαζόμενο χώρο.',
    'footer.rights': 'Με επιφύλαξη κάθε νόμιμου δικαιώματος.',
    'footer.crafted': 'Σχεδιασμένο για ταξιδιώτες που ξέρουν να ταξιδεύουν.',

    // Home
    'home.eyebrow': 'Allure',
    'home.title.line1': 'Το κομμάτι που έλειπε',
    'home.title.line2': 'ανάμεσα στο να μένεις κάπου',
    'home.title.line3': 'και στο να το ζεις πραγματικά.',
    'home.subtitle': 'Το Allure μετατρέπει τις διαμονές σε Airbnb σε επιλεγμένες τοπικές εμπειρίες. Οι επισκέπτες μπαίνουν μέσα από ένα δίκτυο εμπιστοσύνης — hosts, venues και εμπειρίες σχεδιασμένες για ταξιδιώτες που θέλουν να ζήσουν την περιοχή, όχι απλώς να την επισκεφθούν.',
    'home.cta.createAccount': 'Δημιούργησε λογαριασμό',
    'home.cta.haveAccount': 'Έχω ήδη λογαριασμό',
    'home.cta.download': 'Κατέβασε την εφαρμογή',
    'home.cta.orDownload': 'Ή κατέβασε την εφαρμογή',
    'home.cards.guests.eyebrow': 'Για Επισκέπτες',
    'home.cards.guests.title': 'Ένα Pass. Τα καλύτερα venues της περιοχής.',
    'home.cards.guests.b1': 'QR pass που ξεκλειδώνει αποκλειστικές εκπτώσεις σε επιλεγμένα τοπικά venues',
    'home.cards.guests.b2': 'Ξεκλείδωσε μεγαλύτερες εκπτώσεις όσο εξερευνάς',
    'home.cards.guests.b3': 'Επιλεγμένα από ντόπιους — όχι από τουριστικούς αλγόριθμους',
    'home.cards.guests.b4': 'Ανακάλυψε κρυμμένα διαμάντια, έτοιμα στον χάρτη σου',
    'home.cards.guests.b5': 'Άσε φιλοδώρημα στον host σου μέσα από την εφαρμογή',
    'home.cards.guests.b6': 'Κριτικές μόνο από επαληθευμένους Allure guests',
    'home.cards.hosts.eyebrow': 'Για Hosts',
    'home.cards.hosts.title': 'Βοήθησε τους guests σου να εξερευνήσουν. Κέρδισε καθώς κοιμάσαι.',
    'home.cards.hosts.b1': 'Παθητική προμήθεια όταν οι guests σου χρησιμοποιούν το Allure',
    'home.cards.hosts.b2': 'Αυτόματος συγχρονισμός κρατήσεων μέσω του Airbnb ημερολογίου σου',
    'home.cards.hosts.b3': 'Πρόσφερε μια premium εμπειρία χωρίς επιπλέον δουλειά',
    'home.cards.hosts.b4': 'Ενίσχυσε τις κριτικές και την ικανοποίηση των ταξιδιωτών',
    'home.cards.hosts.b5': 'Δώσε τη δυνατότητα στους guests να σε ευχαριστήσουν με tip μέσα στην εφαρμογή',
    'home.cards.venues.eyebrow': 'Για Venues',
    'home.cards.venues.title': 'Φτάσε σε επαληθευμένους ταξιδιώτες. Μετάτρεψε τον τουρισμό σε έσοδα.',
    'home.cards.venues.b1': 'Γίνε από τα λίγα μέρη που οι guests καθοδηγούνται συστηματικά να επισκεφτούν',
    'home.cards.venues.b2': 'Φτάσε σε επαληθευμένους ταξιδιώτες που ήδη διαμένουν κοντά σου',
    'home.cards.venues.b3': 'Αναγνωρίσου ως ένα από τα ξεχωριστά venues της περιοχής',
    'home.cards.venues.b4': 'Μετάτρεψε τον τοπικό τουρισμό σε άμεσα έσοδα εντός του venue',
    'home.privacy.noAdTracking': 'Χωρίς ad tracking',
    'home.privacy.noSellData': 'Δεν πουλάμε δεδομένα χρηστών',
    'home.privacy.gdpr': 'Συμμόρφωση GDPR + CCPA',
    'home.privacy.deletion': 'Πλήρης διαγραφή λογαριασμού μέσα από την εφαρμογή',
    'home.vision.eyebrow': 'Το Όραμα',
    'home.vision.title': 'Οι επισκέπτες δεν φτάνουν πια χαμένοι. Οι hosts δεν βασίζονται πια μόνο στις κρατήσεις. Τα κρυμμένα τοπικά διαμάντια δεν μένουν πια κρυμμένα.',
    'home.vision.body': 'Συνδέοντας ταξιδιώτες, hosts και προσεκτικά επιλεγμένα venues, το Allure δημιουργεί ένα οικοσύστημα φιλοξενίας όπου όλοι κερδίζουν μέσα από εμπιστοσύνη, ανακάλυψη και αξιομνημόνευτες εμπειρίες.',
    'home.vision.footer1': 'Το Allure κάνει roll-out σε ολόκληρη την Ευρώπη με invite-only μοντέλο.',
    'home.vision.footer2': 'Ταξίδεψε σαν ντόπιος. Νιώσε σαν στο σπίτι σου, όπου κι αν μένεις.',
    'home.finalCta.eyebrow': 'Σχεδιασμένο για να το Ζήσεις',
    'home.finalCta.title': 'Έχεις ήδη πρόσκληση; Άνοιξε την εφαρμογή και μπες μέσα.',

    // About
    'about.eyebrow': 'Σχετικά με εμάς',
    'about.title': 'Το Allure υπάρχει για τον ταξιδιώτη που δεν συμβιβάζεται με το προφανές.',
    'about.p1': 'Ξεκινήσαμε το Allure γιατί η απόσταση ανάμεσα στο να μένεις κάπου και στο να το ζεις πραγματικά είχε γίνει τεράστια. Οι αλγόριθμοι έγιναν πιο θορυβώδεις. Οι κριτικές πιο αδιάφορες. Οι αληθινές συστάσεις χάθηκαν.',
    'about.p2': 'Έτσι φτιάξαμε μια ιδιωτική guest πλατφόρμα. Ο host σε προσκαλεί στο Allure μόλις επιβεβαιωθεί η διαμονή σου. Μέσα ανακαλύπτεις τα venues στα οποία θα σε έστελνε πραγματικά ένας ντόπιος — και σκανάρεις το QR σου στο checkout για να ξεκλειδώσεις εκπτώσεις που μόνο οι Allure guests μπορούν να αποκτήσουν. Χωρίς θόρυβο. Χωρίς παγίδες. Μόνο φιλοξενία, όπως αρχικά είχε νόημα.',
    'about.values.title': 'Σε τι πιστεύουμε.',
    'about.values.curated.title': 'Curated, όχι μαζικό',
    'about.values.curated.body': 'Κάθε venue στο Allure επιλέγεται χειροκίνητα από ντόπιους που πραγματικά μένουν, τρώνε και πίνουν στην περιοχή. Χωρίς πληρωμένες κατατάξεις. Χωρίς τουριστικές παγίδες.',
    'about.values.trust.title': 'Εμπιστοσύνη εκ σχεδιασμού',
    'about.values.trust.body': 'Οι κριτικές γράφονται μόνο από επαληθευμένους Allure guests. Οι εκπτώσεις τηρούνται στο σημείο, γιατί κάθε venue συνεργάζεται απευθείας μαζί μας — σκανάρεις το QR σου στον χώρο και εξοικονομείς άμεσα.',
    'about.values.hospitality.title': 'Φτιαγμένο με φιλοξενία',
    'about.values.hospitality.body': 'Χτισμένο μαζί με hosts, εστιατόρια και ταξιδιώτες — τους ανθρώπους για τους οποίους υπάρχει το Allure. Τα σχόλιά τους διαμορφώνουν κάθε νέα έκδοση.',

    // Contact
    'contact.eyebrow': 'Επικοινωνία',
    'contact.title': 'Πες γεια.',
    'contact.subtitle': 'Hosts, venues και περίεργοι ταξιδιώτες — διαβάζουμε κάθε μήνυμα. Στείλε μας γραμμή ή τηλεφώνησε απευθείας.',
    'contact.form.name': 'Το όνομά σου',
    'contact.form.email': 'Email',
    'contact.form.message': 'Μήνυμα',
    'contact.form.namePlaceholder': 'Αλέξης Παπαδόπουλος',
    'contact.form.emailPlaceholder': 'esy@example.com',
    'contact.form.messagePlaceholder': 'Πες μας λίγα λόγια για τον λόγο επικοινωνίας.',
    'contact.form.submit': 'Αποστολή μηνύματος',
    'contact.form.submitting': 'Αποστολή…',
    'contact.form.completeAll': 'Παρακαλώ συμπλήρωσε όλα τα πεδία',
    'contact.form.success': 'Ευχαριστούμε — θα επικοινωνήσουμε σύντομα.',
    'contact.form.error': 'Δεν ήταν δυνατή η αποστολή του μηνύματος αυτή τη στιγμή',
    'contact.aside.eyebrow': 'Απευθείας επαφή',
    'contact.aside.title': 'Μίλα με άνθρωπο, όχι με chatbot.',
    'contact.aside.reply': 'Συνήθως απαντάμε εντός μίας εργάσιμης ημέρας.',

    // Support (marketing)
    'support.eyebrow': 'Υποστήριξη',
    'support.title': 'Είμαστε εδώ όποτε μας χρειαστείς.',
    'support.subtitle': 'Οι περισσότερες απαντήσεις βρίσκονται παρακάτω. Αν κάτι παραμένει ασαφές, στείλε μας μήνυμα απευθείας και θα απαντήσουμε εντός μίας εργάσιμης ημέρας.',
    'support.card1.title': 'Στείλε μας μήνυμα',
    'support.card1.body': 'Μπες στη φόρμα επικοινωνίας κι εμείς θα το δρομολογήσουμε στη σωστή ομάδα.',
    'support.card1.cta': 'Άνοιξε τη φόρμα επικοινωνίας',
    'support.card2.title': 'Υποστήριξη μέσω email',
    'support.card2.body': 'Προτιμάς email; Επικοινώνησε ανά πάσα στιγμή στο contact@allurexp.com',
    'support.card2.cta': 'contact@allurexp.com',
    'support.card3.title': 'Διάβασε το FAQ',
    'support.card3.body': 'Γρήγορες απαντήσεις στις πιο συνηθισμένες ερωτήσεις.',
    'support.card3.cta': 'Πήγαινε στο FAQ',
    'support.faq.title': 'Συχνές ερωτήσεις.',
    'support.faq1.q': 'Πώς μπαίνω στο Allure;',
    'support.faq1.a': 'Όταν κλείνεις ένα Airbnb του οποίου ο host είναι στο Allure, εκείνος σου στέλνει πρόσκληση για να μπεις. Την αποδέχεσαι, φτιάχνεις τον λογαριασμό σου, και είσαι μέσα για όλη τη διάρκεια της διαμονής.',
    'support.faq2.q': 'Πώς λαμβάνω την έκπτωση σε ένα venue;',
    'support.faq2.a': 'Άνοιξε την εφαρμογή Allure, διάλεξε το venue από τον χάρτη και πλήρωσε τον λογαριασμό σου κανονικά στον χώρο. Στο checkout, δείξε στο προσωπικό το QR σου μέσα στην εφαρμογή — το σκανάρουν και η έκπτωση Allure εφαρμόζεται άμεσα.',
    'support.faq3.q': 'Πρέπει να κατεβάσω την εφαρμογή από το Google Play;',
    'support.faq3.a': 'Μπορείς να χρησιμοποιήσεις το Allure στο web στο allurexp.com ή να κατεβάσεις την επίσημη Android εφαρμογή από το Google Play. Και οι δύο μοιράζονται τον ίδιο λογαριασμό.',
    'support.faq4.q': 'Οι εκπτώσεις είναι πραγματικά αποκλειστικές;',
    'support.faq4.a': 'Ναι — τα venues τηρούν την έκπτωση Allure επειδή συνεργάζονται απευθείας μαζί μας. Δεν θα βρεις αυτές τις τιμές δημόσια online.',
    'support.faq5.q': 'Είμαι host — πώς μπαίνω;',
    'support.faq5.a': 'Πάτησε "Ξεκίνα" και επίλεξε Host στην οθόνη ρόλου. Θα χρειαστείς το URL της Airbnb καταχώρησής σου και τον σύνδεσμο iCal ώστε το Allure να εντοπίζει τις κρατήσεις και να προσκαλεί αυτόματα τους guests σου.',
    'support.faq6.q': 'Έχω venue. Πώς γίνομαι Allure Partner;',
    'support.faq6.a': 'Πάτησε "Ξεκίνα", επίλεξε Partner και μετά Venue, και η ομάδα μας εξετάζει προσωπικά κάθε αίτηση.',

    // Language switcher
    'lang.label': 'Γλώσσα',
    'lang.english': 'English',
    'lang.greek': 'Ελληνικά',
  },
};
